import React, { useEffect, useState } from "react";
import { api } from "../../lib/api.js";
import { fmtDateTime } from "../../lib/format.js";
import { Send, Mail, Eye, RefreshCw, Clock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Painel completo do Digest diário (iter 55).
 * Reutilizável no /admin (tab "Digest diário").
 * Inclui: config + envio manual + preview + gestão multi-destinatários.
 */
export default function DailyDigestPanel() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingRecips, setSavingRecips] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [lastSend, setLastSend] = useState(null);
  const [err, setErr] = useState("");

  const loadAll = async () => {
    setBusy(true);
    setErr("");
    try {
      const [s, p, r] = await Promise.all([
        api.get("/reports/daily-digest/status"),
        api.get("/reports/daily-digest/preview"),
        api.get("/reports/daily-digest/recipients"),
      ]);
      setStatus(s.data);
      setPreview(p.data);
      setRecipients(r.data.recipients || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao carregar informação do digest.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const persistRecipients = async (next) => {
    setSavingRecips(true);
    try {
      const { data } = await api.put("/reports/daily-digest/recipients", { recipients: next });
      setRecipients(data.recipients || []);
      toast.success("Destinatários actualizados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao guardar destinatários");
    } finally {
      setSavingRecips(false);
    }
  };

  const addRecipient = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    if (recipients.some((r) => r.email === email)) {
      toast.error("Já existe esse destinatário");
      return;
    }
    const next = [...recipients, { email, name: newName.trim(), active: true }];
    setNewEmail("");
    setNewName("");
    await persistRecipients(next);
  };

  const removeRecipient = async (email) => {
    if (!window.confirm(`Remover ${email} da lista de destinatários?`)) return;
    await persistRecipients(recipients.filter((r) => r.email !== email));
  };

  const toggleActive = async (email) => {
    const next = recipients.map((r) =>
      r.email === email ? { ...r, active: !r.active } : r
    );
    await persistRecipients(next);
  };

  const sendNow = async (weekly = false) => {
    const label = weekly ? "resumo semanal" : "digest diário";
    if (!window.confirm(`Enviar ${label} para ${recipients.filter(r => r.active).length} destinatário(s) agora?`)) return;
    if (weekly) setSendingWeekly(true); else setSending(true);
    try {
      const url = weekly ? "/reports/weekly-digest/send" : "/reports/daily-digest/send";
      const { data } = await api.post(url);
      setLastSend({ ...data, kind: weekly ? "weekly" : "daily" });
      const ok = (data.recipients || []).filter((r) => r.status === "sent").length;
      toast.success(`${label} enviado — ${ok} destinatário(s) OK`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Falha ao enviar ${label}.`);
    } finally {
      if (weekly) setSendingWeekly(false); else setSending(false);
    }
  };

  const openPreviewHtml = (weekly = false) => {
    const url = weekly ? "/reports/weekly-digest/preview.html" : "/reports/daily-digest/preview.html";
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api${url}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="daily-digest-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#1F3864]" /> Digest diário e semanal
          </h2>
          <p className="text-xs text-[#6C757D] mt-0.5">
            Resumo automático de itens em atraso enviado às 07:00 (diário) e às segundas 07:00 (semanal), fuso horário Moçambique.
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={busy}
          className="px-3 py-1.5 text-xs rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white flex items-center gap-1.5"
          data-testid="digest-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      {err && (
        <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">
          {err}
        </div>
      )}

      {/* Config */}
      {status && (
        <section className="bg-white border border-gray-200 rounded-sm p-4" data-testid="digest-status-card">
          <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-3 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-[#1F3864]" /> Configuração do scheduler
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <Field label="Estado" value={
              <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-sm ${
                status.enabled && status.scheduler_running ? "bg-[#D4EDDA] text-[#155724]" : "bg-[#F8D7DA] text-[#721C24]"
              }`} data-testid="digest-enabled-badge">
                {status.enabled && status.scheduler_running ? "Activo" : "Inactivo"}
              </span>
            } />
            <Field label="Remetente" value={<span className="font-mono text-xs">{status.from_email || "—"}</span>} />
            <Field label="Horário diário" value={`${String(status.hour).padStart(2, "0")}:${String(status.minute).padStart(2, "0")} · ${status.timezone}`} />
            <Field label="Dia semanal" value={`${status.weekly_day} · ${String(status.hour).padStart(2, "0")}:${String(status.minute).padStart(2, "0")}`} />
            <Field label="Próximo envio diário" value={status.daily_next_run ? fmtDateTime(status.daily_next_run) : "—"} />
            <Field label="Próximo envio semanal" value={status.weekly_next_run ? fmtDateTime(status.weekly_next_run) : "—"} />
          </div>
        </section>
      )}

      {/* Recipients */}
      <section className="bg-white border border-gray-200 rounded-sm p-4">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2">Destinatários</h3>
        <p className="text-xs text-[#6C757D] mb-3">
          Múltiplos emails podem receber cada envio. Nota: com o domínio de teste Resend (<code>onboarding@resend.dev</code>) só o dono da conta recebe efectivamente — verificar um domínio próprio em resend.com/domains para libertar todos os destinatários.
        </p>
        <div className="border border-gray-200 rounded-sm overflow-hidden mb-3">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Nome</th>
                <th className="px-3 py-2 text-center">Activo</th>
                <th className="px-3 py-2 text-right">Acções</th>
              </tr>
            </thead>
            <tbody>
              {recipients.length === 0 ? (
                <tr><td colSpan="4" className="px-3 py-6 text-center text-xs text-[#6C757D]">Sem destinatários. Adiciona pelo menos um em baixo.</td></tr>
              ) : recipients.map((r, i) => (
                <tr key={r.email} className="border-t border-gray-100" data-testid={`digest-recipient-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                  <td className="px-3 py-2 text-xs text-[#6C757D]">{r.name || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => toggleActive(r.email)}
                      disabled={savingRecips}
                      className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-sm ${
                        r.active ? "bg-[#D4EDDA] text-[#155724]" : "bg-[#E5E7EB] text-[#6C757D]"
                      }`}
                      data-testid={`digest-recipient-toggle-${i}`}
                    >
                      {r.active ? "Sim" : "Não"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => removeRecipient(r.email)}
                      disabled={savingRecips}
                      className="p-1 text-[#DC3545] hover:bg-[#F8D7DA] rounded-sm"
                      data-testid={`digest-recipient-remove-${i}`}
                      title="Remover"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Email</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="nome@dominio.com"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864]"
              data-testid="digest-new-recipient-email"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Nome (opcional)</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="ex.: Michel Andrade"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864]"
              data-testid="digest-new-recipient-name"
            />
          </div>
          <button
            onClick={addRecipient}
            disabled={savingRecips}
            className="px-3 py-1.5 text-sm rounded-sm bg-[#1F3864] text-white hover:bg-[#142542] flex items-center gap-1.5 disabled:opacity-60"
            data-testid="digest-new-recipient-add"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>
      </section>

      {/* Manual send */}
      <section className="bg-white border border-gray-200 rounded-sm p-4">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2">Envio manual</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-[#1A1A2E] mb-1">Digest diário</div>
            <p className="text-xs text-[#6C757D] mb-2">Envia agora o resumo de itens em atraso.</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => sendNow(false)}
                disabled={sending || recipients.filter(r=>r.active).length === 0}
                className="px-3 py-1.5 text-sm rounded-sm bg-[#1F3864] text-white hover:bg-[#142542] disabled:opacity-60 flex items-center gap-1.5"
                data-testid="digest-send-now"
              >
                <Send className="w-3.5 h-3.5" /> {sending ? "A enviar…" : "Enviar diário"}
              </button>
              <button
                onClick={() => openPreviewHtml(false)}
                className="px-3 py-1.5 text-sm rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white flex items-center gap-1.5"
                data-testid="digest-preview-html"
              >
                <Eye className="w-3.5 h-3.5" /> Pré-visualizar
              </button>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-[#1A1A2E] mb-1">Resumo semanal</div>
            <p className="text-xs text-[#6C757D] mb-2">Envia agora o resumo desta semana vs. semana anterior.</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => sendNow(true)}
                disabled={sendingWeekly || recipients.filter(r=>r.active).length === 0}
                className="px-3 py-1.5 text-sm rounded-sm bg-[#1F3864] text-white hover:bg-[#142542] disabled:opacity-60 flex items-center gap-1.5"
                data-testid="weekly-send-now"
              >
                <Send className="w-3.5 h-3.5" /> {sendingWeekly ? "A enviar…" : "Enviar semanal"}
              </button>
              <button
                onClick={() => openPreviewHtml(true)}
                className="px-3 py-1.5 text-sm rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white flex items-center gap-1.5"
                data-testid="weekly-preview-html"
              >
                <Eye className="w-3.5 h-3.5" /> Pré-visualizar
              </button>
            </div>
          </div>
        </div>
        {lastSend && (
          <div className="mt-3 px-3 py-2 bg-[#D4EDDA] text-[#155724] border border-[#28A745]/20 rounded-sm text-xs" data-testid="digest-last-send">
            Último envio ({lastSend.kind === "weekly" ? "semanal" : "diário"}): {(lastSend.recipients || []).map((r) => `${r.email} ${r.status === "sent" ? "✓" : "✗"}`).join(" · ")}
          </div>
        )}
      </section>

      {/* Preview counts */}
      {preview && (
        <section>
          <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2">
            Conteúdo do próximo digest diário ({preview.totals.grand} itens)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="SOMs em atraso" value={preview.totals.soms} testid="kpi-digest-soms" />
            <Kpi label="Tarefas em atraso" value={preview.totals.tasks} testid="kpi-digest-tasks" />
            <Kpi label="PECs com ETA passada" value={preview.totals.pecs} testid="kpi-digest-pecs" />
            <Kpi label="SWCs sem movimento" value={preview.totals.swcs} testid="kpi-digest-swcs" />
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-0.5">{label}</div>
      <div className="text-sm text-[#1A1A2E]">{value}</div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  const empty = value === 0;
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className={`text-3xl font-bold font-heading mt-1 ${empty ? "text-[#155724]" : "text-[#1F3864]"}`}>{value}</div>
    </div>
  );
}
